/**
 * Inventory detail panel — mockup `#inv-detail-panel` (fsl-rtm-map-mu.html).
 * Renders the selected user or drone. Drone view mirrors the mockup section
 * order: dTwin strip → Identifiers → Operational Toggles → Pilot & Separation
 * Defaults → Data Source Monitoring → Custodian Config → Conspicuity →
 * Aircraft → DAA Rules → Custodian API Config (YAML) → Notes.
 *
 * Save Configuration issues a single `PUT /config` in live mode with only
 * the fields that changed, plus a separate `PUT /ownership` when the
 * ownership flag moved (`/config` ignores that field and `/ownership` also
 * takes an audit `reason`), and a separate `PUT /adapter-config` when the
 * Adapter Type dropdown changed (ENG-228). This replaced the per-concern `/oi-automation`,
 * `/oi-defaults`, `/adsb-surrogate` and `/tisb-rebroadcast` calls on
 * 2026-08-21 at BE's direction. Mock mode keeps the toast-stub — there's
 * no mock backend for these mutations to hit. Deployment Notes has no backing
 * endpoint anywhere in the collection, so it stays local-only in both modes.
 */
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { AdapterTypeSelect } from '@/features/inv/AdapterTypeSelect';
import { useInvStore } from '@/features/inv/InvStore';
import { adapterChanged, adapterSelectValue, isMavlinkAdapter } from '@/lib/api/adapterTypes';
import {
  CustodianApiError,
  type CustodianConfigPatch,
  type DaaRuleRecord,
  applyConfigToInventoryDrone,
  applyHealthToInventoryDrone,
  missionVolumeVertexCount,
} from '@/lib/api/custodian';
import {
  useActivePilots,
  useAddDaaRule,
  useClearAircraftBuffer,
  useCustodianDroneConfig,
  useCustodianHealth,
  useCustodianMission,
  useDeleteDaaRule,
  useInventoryDrones,
  useInventoryUsers,
  useSaveCustodianConfig,
  useSetAdapterType,
  useSetOwnershipFlag,
} from '@/lib/api/queries';
import type { InventoryDrone, InventoryUser } from '@/lib/api/types';
import { utmStateLabel, utmStateTone } from '@/lib/api/utmState';
import { config } from '@/lib/config';
import { cn } from '@/lib/utils';
import {
  Boxes,
  Check,
  Lock,
  Pencil,
  Radar,
  RadioTower,
  Repeat,
  ShieldCheck,
  Timer,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { bufferKeysToClear } from './bufferWrites';

const STUB = 'Inventory write endpoints land in a future iteration.';
// PUT /ownership requires a `reason` string per the Postman spec, and the UI
// has no field to collect one yet — hardcoded placeholder until product
// defines a real one (e.g. a text prompt on the toggle).
const OWNERSHIP_REASON = 'Updated via FLYsafe v2 Inventory panel';
// Since 2026-08-21 `PUT /config` persists the pilot, the three separation
// scalars and the latency envelope. Still NOT persisted: the NetworkID gate
// Nothing in the panel is local-only as of the 2026-08-27 API update: NetworkID
// streaming became writable and the crewed/uncrewed separation split got real
// columns. Kept for the next field that lands display-only — the save toast
// says so rather than letting an edit disappear without comment.
const LOCAL_ONLY = 'Some edited fields are not persisted by the API yet.';
// The YAML is rendered from GET /config — the Custodian API has no endpoint
// that accepts a raw config file or restarts an agent, so edits are staged in
// the browser only.
const YAML_STUB = 'Staged locally — no Custodian endpoint accepts a raw config or restart yet.';

/** Render '—' when a field has no value (the Custodian API doesn't carry it). */
function dash(v: string | number | null | undefined, suffix = ''): string {
  if (v == null || v === '' || (typeof v === 'number' && Number.isNaN(v))) return '—';
  return `${v}${suffix}`;
}

export function InvDetailPanel() {
  const selUser = useInvStore((s) => s.selectedUserId);
  const selDrone = useInvStore((s) => s.selectedDroneId);
  const { data: users } = useInventoryUsers();
  const { data: drones } = useInventoryDrones();
  const drone = drones?.find((d) => d.drone_id === selDrone);
  const user = users?.find((u) => u.user_id === selUser);

  if (drone) return <DroneDetail key={drone.drone_id} d={drone} />;
  if (user) return <UserDetail key={user.user_id} u={user} />;
  return <Empty />;
}

function Empty() {
  return (
    <div className="flex flex-col h-full">
      <PanelHeader title="Select an item" />
      <div className="flex-1 flex flex-col items-center justify-center gap-2 text-(--color-text3)">
        <Boxes size={28} strokeWidth={1.3} />
        <span className="text-[0.846em]">Select a user or drone to view details</span>
      </div>
    </div>
  );
}

/* ───────────────────────── Drone detail ───────────────────────── */

/** Fallback pilot list for mock mode, and for the live case where
 *  `GET /pilots/active` is unavailable (503 no DLB credentials / 502 DLB down
 *  — see `fetchActivePilots`). Live mode prefers the real list; these names
 *  are invented, so they must never be the source the picker writes from when
 *  a real one exists. The dropdown resolves name → GUID from whichever list is
 *  in play so the GUID is never free-typed. */
const PILOT_REGISTRY: { name: string; fs_guid: string }[] = [
  { name: 'Sarah Chen', fs_guid: 'pilot-8f3a2e19-4c1a-4e6b-9c17-2b9e5d3f7a0c' },
  { name: 'Marcus Webb', fs_guid: 'pilot-3c7d9a41-8e2f-4b6c-a1d5-7f9e2c4b8a63' },
  { name: 'Devon Park', fs_guid: 'pilot-1a5f8e23-6b9c-4d7a-b2e8-3f6a9c1d5e47' },
  { name: 'Alicia Nguyen', fs_guid: 'pilot-6d2b9f47-3a8c-4e15-b7d9-1c5a8e3f6b92' },
];

/** Editable numeric parameters, with the ranges the Custodian doc documents.
 *  `api` names the `PUT /config` field a param maps to.
 *
 *  Every param here now has one: BE split its two separation scalars into the
 *  eight crewed/uncrewed x horizontal/vertical columns on 2026-08-27, so the
 *  grid that used to be editable-but-discarded is a real write path. The
 *  scalars themselves are gone from PARAMS — `InventoryDrone` still derives
 *  them for display, but they mirror the crewed-horizontal fields and writing
 *  both would send the same value twice under two names. */
const PARAMS = {
  horizontal_speed_ms: { api: 'horizontal_speed_ms' },
  vertical_speed_ms: { api: 'vertical_speed_ms' },
  oi_automation_square_m: { api: 'oi_automation_square_m', min: 350, max: 1400 },
  oi_automation_radius_m: { api: 'oi_automation_radius_m', min: 200, max: 800 },
  oi_automation_amend_min_trigger_m: {
    api: 'oi_automation_amend_min_trigger_m',
    min: 50,
    max: 200,
  },
  // ENG-285 — metres AGL. The API takes any value above 0 with no upper limit,
  // so there is no max; 1 m is the floor (a 0 would be a 422).
  oi_automation_amend_max_alt_agl_m: { api: 'oi_automation_amend_max_alt_agl_m', min: 1 },
  // ENG-286 — whole seconds, the API's ranges.
  oi_automation_time_amend_trigger_s: {
    api: 'oi_automation_time_amend_trigger_s',
    min: 60,
    max: 1800,
    int: true,
  },
  oi_automation_time_amend_extension_s: {
    api: 'oi_automation_time_amend_extension_s',
    min: 60,
    max: 3600,
    int: true,
  },
  fv_lateral_buffer_m: { api: 'fv_buffer_m' },
  cv_lateral_buffer_m: { api: 'cv_buffer_m' },
  waypoint_buffer_m: { api: 'waypoint_buffer_m', min: 5, max: 500 },
  // ENG-295 — the API documents only ">= 0" (422 below), so no upper bound.
  geofence_buffer_m: { api: 'geofence_buffer_m', min: 0 },
  obstacle_buffer_m: { api: 'obstacle_buffer_m', min: 0 },
  nmac_h_crewed_m: { api: 'nmac_crewed_h' },
  nmac_v_crewed_m: { api: 'nmac_crewed_v' },
  nmac_h_uncrewed_m: { api: 'nmac_uncrewed_h' },
  nmac_v_uncrewed_m: { api: 'nmac_uncrewed_v' },
  swc_h_crewed_m: { api: 'swc_crewed_h' },
  swc_v_crewed_m: { api: 'swc_crewed_v' },
  swc_h_uncrewed_m: { api: 'swc_uncrewed_h' },
  swc_v_uncrewed_m: { api: 'swc_uncrewed_v' },
  eval_distance_m: { api: 'eval_distance_m' },
  data_source_max_latency_ms: { api: 'data_source_max_latency_ms' },
} satisfies Record<string, { api?: string; min?: number; max?: number; int?: boolean }>;

type ParamKey = keyof typeof PARAMS;
type ParamState = Record<ParamKey, string>;

function paramsFrom(d: InventoryDrone): ParamState {
  const out = {} as ParamState;
  for (const k of Object.keys(PARAMS) as ParamKey[]) {
    const v = d[k];
    out[k] = v == null ? '' : String(v);
  }
  return out;
}

function DroneDetail({ d: base }: { d: InventoryDrone }) {
  // `/config` — per-drone, only fetched for whichever drone is open here.
  // Fills host/port/protocol, c2_links, video_feeds, surveillance mode and
  // tc_reg, which the list endpoint doesn't carry. `/health` fills the GCS
  // plugin session/version, which nothing else carries.
  const { data: cfg } = useCustodianDroneConfig(base.serial_number);
  const { data: health } = useCustodianHealth(base.serial_number);
  const pilots = useActivePilots();
  let d = cfg ? applyConfigToInventoryDrone(base, cfg) : base;
  if (health) d = applyHealthToInventoryDrone(d, health);

  const [dirty, setDirty] = useState(false);
  const [ownership, setOwnership] = useState(d.ownership_flag_enabled);
  const [oiAuto, setOiAuto] = useState(d.oi_automation_enabled);
  const [timeAmend, setTimeAmend] = useState(d.oi_automation_time_amend_enabled);
  const [surrogate, setSurrogate] = useState(d.adsb_surrogate_enabled);
  const [netid, setNetid] = useState(d.networkid_streaming_enabled);
  const [tisb, setTisb] = useState(d.tisb_rebroadcast_enabled);
  const [adsbOut, setAdsbOut] = useState(d.adsb_out === 'Enabled');
  const [pilot, setPilot] = useState(d.default_pilot_name ?? '');
  const [adsbCode, setAdsbCode] = useState(d.adsb_identification_code ?? '');
  const [adsbCodeError, setAdsbCodeError] = useState<string | null>(null);
  const [adapter, setAdapter] = useState(adapterSelectValue(d.adapter_type));
  const [params, setParams] = useState<ParamState>(() => paramsFrom(d));
  const touch = () => setDirty(true);

  // `useState`'s initializer only runs once at mount — it can capture a
  // still-in-flight query's stale value if `cfg`/`base` resolve just after
  // first render. Resync whenever the server value actually changes, but
  // only while there's no unsaved local edit to avoid clobbering it.
  const serverParams = JSON.stringify(paramsFrom(d));
  useEffect(() => {
    if (dirty) return;
    setOwnership(d.ownership_flag_enabled);
    setOiAuto(d.oi_automation_enabled);
    setTimeAmend(d.oi_automation_time_amend_enabled);
    setSurrogate(d.adsb_surrogate_enabled);
    setNetid(d.networkid_streaming_enabled);
    setTisb(d.tisb_rebroadcast_enabled);
    setAdsbOut(d.adsb_out === 'Enabled');
    setPilot(d.default_pilot_name ?? '');
    setAdsbCode(d.adsb_identification_code ?? '');
    setAdapter(adapterSelectValue(d.adapter_type));
    setParams(JSON.parse(serverParams) as ParamState);
  }, [
    d.ownership_flag_enabled,
    d.oi_automation_enabled,
    d.oi_automation_time_amend_enabled,
    d.adsb_surrogate_enabled,
    d.networkid_streaming_enabled,
    d.tisb_rebroadcast_enabled,
    d.adsb_out,
    d.default_pilot_name,
    d.adsb_identification_code,
    d.adapter_type,
    serverParams,
    dirty,
  ]);

  const setParam = (k: ParamKey, v: string) => {
    setParams((p) => ({ ...p, [k]: v }));
    touch();
  };

  /** Clamp to the documented range on blur, and keep CV ≥ FV — a contingency
   *  volume inside the flight volume isn't a contingency. */
  const commitParam = (k: ParamKey) => {
    setParams((p) => {
      const spec = PARAMS[k] as { min?: number; max?: number; int?: boolean };
      const raw = Number.parseFloat(p[k]);
      if (!Number.isFinite(raw)) return { ...p, [k]: '' };
      let v = raw;
      if (spec.min != null) v = Math.max(spec.min, v);
      if (spec.max != null) v = Math.min(spec.max, v);
      if (spec.int) v = Math.round(v);
      const next = { ...p, [k]: String(v) };
      if (k === 'cv_lateral_buffer_m' || k === 'fv_lateral_buffer_m') {
        const fv = Number.parseFloat(next.fv_lateral_buffer_m);
        const cv = Number.parseFloat(next.cv_lateral_buffer_m);
        if (Number.isFinite(fv) && Number.isFinite(cv) && cv < fv)
          next.cv_lateral_buffer_m = String(fv);
      }
      return next;
    });
  };

  // Live mode draws the picker from `GET /pilots/active`; mock mode keeps the
  // stand-in list. Crucially live mode does NOT fall back to PILOT_REGISTRY
  // when the endpoint is down: mock mode's Save is a toast stub, but live mode
  // writes for real, so offering an invented name there would PUT a fabricated
  // `default_pilot_fs_guid` onto an actual aircraft. An empty picker is the
  // honest state — `pilotsUnavailable` labels it so it doesn't read as
  // "this tenant has no pilots".
  //
  // The stored pilot is appended when the list doesn't carry it, so a value BE
  // already holds still renders as the selected option instead of falling
  // through to "— Select —" — a controlled <select> whose value matches no
  // <option> shows as unselected, which reads as "no pilot assigned" for a
  // drone that has one.
  const livePilots = pilots.data;
  const pilotOptions = useMemo(() => {
    const list = config.isMock
      ? PILOT_REGISTRY
      : (livePilots ?? [])
          .map((p) => ({ name: p.full_name as string, fs_guid: p.pilot_guid as string }))
          .sort((a, b) => a.name.localeCompare(b.name));
    const stored = d.default_pilot_name;
    if (stored && !list.some((p) => p.name === stored)) {
      return [...list, { name: stored, fs_guid: d.default_pilot_fs_guid ?? '' }];
    }
    return list;
  }, [livePilots, d.default_pilot_name, d.default_pilot_fs_guid]);

  const pilotsUnavailable = !config.isMock && pilots.isError;
  // `/pilots/active` reads through to DLB and pages its /user API on every
  // call — measured 13-20 s against production, longer under load, with no
  // server-side cache. An empty picker for that long is indistinguishable
  // from "this tenant has no pilots", so the wait is labelled.
  const pilotsLoading = !config.isMock && pilots.isPending;

  // Clearing the pilot must clear the GUID with it. Falling back to the
  // stored GUID unconditionally left an orphan behind: selecting "— Select —"
  // sent an empty name alongside the previous pilot's GUID. The stored value
  // is only a sensible fallback when a name IS set but isn't in the list
  // (e.g. BE holds a pilot the endpoint no longer returns).
  const pilotGuid = pilot
    ? (pilotOptions.find((p) => p.name === pilot)?.fs_guid ?? d.default_pilot_fs_guid)
    : null;

  const setOwnershipFlag = useSetOwnershipFlag();
  const saveConfig = useSaveCustodianConfig();
  const setAdapterType = useSetAdapterType();
  const clearBuffer = useClearAircraftBuffer();
  const saving =
    setOwnershipFlag.isPending ||
    saveConfig.isPending ||
    setAdapterType.isPending ||
    clearBuffer.isPending;

  const onSave = async () => {
    if (config.isMock) {
      toast.success(`Configuration saved — ${d.drone_id}`, { description: STUB });
      setDirty(false);
      return;
    }
    // ENG-302 — RTM can only be on once the OI geometry is filled in; the API
    // answers 422 otherwise. Same check here, naming what is missing.
    if (oiAuto && !d.oi_automation_enabled) {
      const missing = missingOiGeometry(params);
      if (missing.length > 0) {
        toast.error(`Fill in ${missing.join(', ')} before turning on OI Automation.`);
        return;
      }
    }
    // ENG-304 — the API rejects these two states, so catch them before the trip.
    // They are the same rules and the same words it answers with.
    if (adsbOut && !adsbCode) {
      setAdsbCodeError(ADSB_CODE_REQUIRED);
      toast.error(ADSB_CODE_REQUIRED);
      return;
    }
    if (adsbOut && tisb) {
      toast.error(ADSB_AND_TISB);
      return;
    }
    try {
      // Only send parameters that map to a `PUT /config` field AND actually
      // changed — the endpoint 400s on an explicit null, so anything unset
      // must be omitted rather than sent as null.
      const patch: CustodianConfigPatch = {};
      let localOnlyEdited = false;
      for (const k of Object.keys(PARAMS) as ParamKey[]) {
        const api = (PARAMS[k] as { api?: string }).api;
        const v = Number.parseFloat(params[k]);
        const changed = Number.isFinite(v) && v !== d[k];
        if (!api) {
          // No param is local-only since the 2026-08-27 separation split, but
          // the branch stays: PARAMS is the one place a display-only field
          // would be added, and silently dropping its edit is worse than
          // telling the user it didn't persist.
          if (changed) localOnlyEdited = true;
          continue;
        }
        if (!changed) continue;
        (patch as Record<string, number>)[api] = v;
      }
      if (oiAuto !== d.oi_automation_enabled) patch.oi_automation_enabled = oiAuto;
      if (timeAmend !== d.oi_automation_time_amend_enabled)
        patch.oi_automation_time_amend_enabled = timeAmend;
      if (surrogate !== d.adsb_surrogate_enabled) patch.adsb_surrogate_enabled = surrogate;
      if (tisb !== d.tisb_rebroadcast_enabled) patch.tisb_rebroadcast_enabled = tisb;
      if (adsbOut !== (d.adsb_out === 'Enabled')) patch.adsb_out_enabled = adsbOut;
      // Writable as of the 2026-08-27 API update — previously accepted and
      // silently discarded, so it was tracked as a local-only edit instead.
      if (netid !== d.networkid_streaming_enabled) patch.networkid_streaming_enabled = netid;
      // `""` is how the API clears the code, so an emptied box is a real edit;
      // an untouched one is left out and stays as it is.
      if (adsbCode !== (d.adsb_identification_code ?? '')) {
        patch.adsb_identification_code = adsbCode;
      }
      if (pilot !== (d.default_pilot_name ?? '')) {
        patch.default_pilot_name = pilot;
        patch.default_pilot_fs_guid = pilotGuid ?? '';
      }

      // Only PUT /config when something it owns actually changed — the
      // endpoint rejects an empty body with a 400. Toggling ONLY the
      // ownership flag produced exactly that: `/ownership` succeeded, the
      // empty `/config` PUT 400'd, and the panel reported a failed save and
      // stayed dirty over a change the server had already accepted.
      // ENG-295 — an emptied buffer is its own request (`/config` ignores null),
      // and must land BEFORE the config save below: that save re-reads the drone
      // and overwrites the list entry, so a read that overtook the clear would
      // bring the old value back.
      for (const key of bufferKeysToClear(params, d)) {
        await clearBuffer.mutateAsync({ serial: base.serial_number, key });
      }

      const calls: Promise<unknown>[] = [];
      if (Object.keys(patch).length > 0) {
        calls.push(saveConfig.mutateAsync({ serial: base.serial_number, patch }));
      }
      // Compare against `d` (the `/config`-sourced value, kept fresh via
      // setQueryData on every successful save), NOT `base` (the drones-list
      // prop) — `base` is never invalidated after an ownership change (see
      // useSetOwnershipFlag's comment), so after the first save it silently
      // freezes at the page-load value. Comparing against it meant toggling
      // on then back off skipped the second PUT entirely: `ownership`(false)
      // vs stale `base.ownership_flag_enabled`(also false, page-load value)
      // read as "unchanged", so the revert never reached the server — the
      // toggle then visibly snapped back to the still-true cached value.
      if (ownership !== d.ownership_flag_enabled) {
        calls.push(
          setOwnershipFlag.mutateAsync({
            serial: base.serial_number,
            enabled: ownership,
            reason: OWNERSHIP_REASON,
          }),
        );
      }
      // The adapter type has its own endpoint (`PUT /adapter-config`, ENG-228).
      // Only written when it actually changed: `mavlink` stored with `MAVLink`
      // selected is the same adapter and must not re-spell the stored value.
      if (adapterChanged(d.adapter_type, adapter)) {
        calls.push(
          setAdapterType.mutateAsync({ serial: base.serial_number, adapterType: adapter }),
        );
      }
      await Promise.all(calls);
      toast.success(`Configuration saved — ${d.drone_id}`, {
        description: localOnlyEdited ? LOCAL_ONLY : undefined,
      });
      setAdsbCodeError(null);
      setDirty(false);
    } catch (err) {
      // A 422 says what is wrong in its own words (e.g. a malformed ADS-B code);
      // show it next to the field and in the toast instead of a bare failure.
      if (err instanceof CustodianApiError && err.status === 422 && err.detail) {
        if (/identification code/i.test(err.detail)) setAdsbCodeError(err.detail);
        toast.error(err.detail);
      } else {
        // Say what the server answered — a bare "failed" hides whether it is a
        // rejected value, an older Custodian that doesn't know a field, or an outage.
        toast.error(`Failed to save configuration — ${d.drone_id}`, {
          description:
            err instanceof CustodianApiError
              ? [err.message, err.detail].filter(Boolean).join(' — ')
              : err instanceof Error
                ? err.message
                : undefined,
        });
      }
    }
  };

  return (
    <div className="flex flex-col h-full">
      <PanelHeader title={d.display_name}>
        <StatusChip
          ok={d.custodian_configured && d.agent_deployed}
          warn={d.agent_deployed && !d.custodian_configured}
          label={
            d.agent_deployed
              ? `✓ Deployed${d.custodian_runtime ? ` · ${d.custodian_runtime}` : ''}`
              : '✗ Not Deployed'
          }
        />
        <StatusChip
          ok={d.custodian_configured}
          warn={!d.custodian_configured}
          label={d.custodian_configured ? '✓ Configured' : '⚠ Not Configured'}
        />
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={onSave}
          className={cn(
            'h-6 px-2.5 rounded-md text-[0.692em] font-bold flex items-center gap-1 shrink-0 transition-colors',
            dirty && !saving
              ? 'bg-(--color-green) text-(--color-on-accent)'
              : 'bg-(--color-bg) text-(--color-text3) cursor-not-allowed',
          )}
        >
          <Check size={11} /> {saving ? 'Saving…' : 'Save Configuration'}
        </button>
      </PanelHeader>

      <div className="flex-1 overflow-y-auto p-3.5 space-y-6">
        <DTwinStrip d={d} />

        <Sec title="Identifiers">
          <FieldGrid>
            <Field label="OEM Serial Number" value={d.serial_number} mono />
            <Field label="TC Registration" value={d.registration ?? '—'} mono />
            <Field label="Call Sign" value={d.ec_callsign ?? '—'} mono />
            <Field label="Flight Controller S/N" value={d.flight_controller_serial || '—'} mono />
          </FieldGrid>
        </Sec>

        {/* `GET /{serial}/mission` — the active binding from `flights.binding`.
            Only 3 of 27 stage aircraft have one; the rest 404, which is the
            ordinary "no binding" case and renders as an explicit empty state
            rather than a hidden section. */}
        <Sec title="Mission Binding">
          <MissionBinding serial={d.serial_number} />
        </Sec>

        {/* Accordion — each toggle's parameters expand inline beneath it and
            collapse when it's switched off. ADS-B Out/In are deliberately not
            here: they're provisioning state, configured under Conspicuity. */}
        <Sec title="Operational Toggles">
          <div className="rounded-lg border border-(--color-border) overflow-hidden">
            <OpsRow
              icon={<Zap size={15} />}
              tone="blue"
              name="OI Automation"
              meta={
                oiAuto
                  ? 'Declaring OIs to RTM — switching off ends any active OI'
                  : 'RTM off — no OI is declared, requests are held'
              }
              on={oiAuto}
              onToggle={() => {
                setOiAuto((v) => !v);
                touch();
              }}
            />
            {oiAuto && (
              <div className="bg-(--color-bg) border-b border-(--color-border-lite)">
                <SubLabel>Speed Defaults</SubLabel>
                <ParamGrid>
                  <NumField
                    label="H Speed"
                    unit="m/s"
                    k="horizontal_speed_ms"
                    params={params}
                    onEdit={setParam}
                    onCommit={commitParam}
                  />
                  <NumField
                    label="V Speed"
                    unit="m/s"
                    k="vertical_speed_ms"
                    params={params}
                    onEdit={setParam}
                    onCommit={commitParam}
                  />
                </ParamGrid>
                <SubLabel>OI Geometry</SubLabel>
                <ParamGrid>
                  <NumField
                    label="Square"
                    unit="m"
                    k="oi_automation_square_m"
                    params={params}
                    onEdit={setParam}
                    onCommit={commitParam}
                  />
                  <NumField
                    label="Radius"
                    unit="m"
                    k="oi_automation_radius_m"
                    params={params}
                    onEdit={setParam}
                    onCommit={commitParam}
                  />
                  <NumField
                    label="Amend Trigger"
                    unit="m"
                    k="oi_automation_amend_min_trigger_m"
                    params={params}
                    onEdit={setParam}
                    onCommit={commitParam}
                  />
                  <NumField
                    label="Amend Max AGL"
                    unit="m"
                    note={feetNote(params.oi_automation_amend_max_alt_agl_m)}
                    k="oi_automation_amend_max_alt_agl_m"
                    params={params}
                    onEdit={setParam}
                    onCommit={commitParam}
                  />
                </ParamGrid>
                <div className="px-3.5 pb-2 text-[0.692em] text-(--color-text3)">
                  Max AGL is in metres (400 ft = 121.92 m) and applies from the next OI declaration;
                  an OI already active keeps its altitude.
                </div>
                <SubLabel>FV / CV Buffers (Horizontal)</SubLabel>
                <ParamGrid>
                  <NumField
                    label="FV Lateral"
                    unit="m"
                    k="fv_lateral_buffer_m"
                    params={params}
                    onEdit={setParam}
                    onCommit={commitParam}
                  />
                  <NumField
                    label="CV Lateral"
                    unit="m"
                    k="cv_lateral_buffer_m"
                    params={params}
                    onEdit={setParam}
                    onCommit={commitParam}
                  />
                </ParamGrid>
                <ParamGrid>
                  <NumField
                    label="Waypoint Buffer"
                    unit="m"
                    k="waypoint_buffer_m"
                    params={params}
                    onEdit={setParam}
                    onCommit={commitParam}
                  />
                </ParamGrid>
              </div>
            )}
            <OpsRow
              icon={<Timer size={15} />}
              tone="blue"
              name="OI Time Extension"
              meta={
                timeAmend
                  ? 'Extends the OI while the aircraft is still flying'
                  : 'Off — the OI ends at its planned time'
              }
              on={timeAmend}
              onToggle={() => {
                setTimeAmend((v) => !v);
                touch();
              }}
            />
            {timeAmend && (
              <div className="bg-(--color-bg) border-b border-(--color-border-lite)">
                <ParamGrid>
                  <NumField
                    label="Extend Before End"
                    unit="s"
                    note={minutesNote(params.oi_automation_time_amend_trigger_s)}
                    k="oi_automation_time_amend_trigger_s"
                    params={params}
                    onEdit={setParam}
                    onCommit={commitParam}
                  />
                  <NumField
                    label="Extend By"
                    unit="s"
                    note={minutesNote(params.oi_automation_time_amend_extension_s)}
                    k="oi_automation_time_amend_extension_s"
                    params={params}
                    onEdit={setParam}
                    onCommit={commitParam}
                  />
                </ParamGrid>
                <div className="px-3.5 pb-2 text-[0.692em] text-(--color-text3)">
                  A change applies to a flight already in the air within a few seconds. At most 15
                  min is added per OI.
                </div>
              </div>
            )}
            <OpsRow
              icon={<Radar size={15} />}
              tone="blue"
              name="NetworkID Streaming"
              meta={
                netid
                  ? 'Drone telemetry is processed normally'
                  : 'Telemetry from this drone is ignored and any active OI ends'
              }
              on={netid}
              onToggle={() => {
                setNetid((v) => !v);
                touch();
              }}
            />
            <OpsRow
              icon={<RadioTower size={15} />}
              tone="green"
              name="ADS-B Out Broadcast"
              meta={
                surrogate
                  ? LIVEADS_NA
                  : adsbOut
                    ? 'Broadcasting this aircraft over ADS-B Out'
                    : !adsbCode
                      ? 'Add an ADS-B Identification Code to turn this on'
                      : tisb
                        ? 'Turn TIS-B Rebroadcast off first — they cannot both be on'
                        : 'Not broadcasting over ADS-B Out'
              }
              on={adsbOut}
              disabled={surrogate || (!adsbOut && (!adsbCode || tisb))}
              onToggle={() => {
                setAdsbOut((v) => !v);
                touch();
              }}
            />
            <OpsRow
              icon={<Repeat size={15} />}
              tone="violet"
              name="TIS-B Rebroadcast"
              meta={
                surrogate
                  ? LIVEADS_NA
                  : tisb
                    ? 'Rebroadcasting telemetry into the TIS-B feed'
                    : adsbOut
                      ? 'Turn ADS-B Out off first — they cannot both be on'
                      : 'Not participating in TIS-B rebroadcast'
              }
              on={tisb}
              disabled={surrogate || (!tisb && adsbOut)}
              onToggle={() => {
                setTisb((v) => !v);
                touch();
              }}
            />
            <OpsRow
              icon={<RadioTower size={15} />}
              tone="violet"
              name="ADS-B Surrogate"
              meta={
                surrogate
                  ? 'Telemetry source is ADS-B (LiveADS) — ADS-B Out and TIS-B are off'
                  : 'Native NetworkID source used — no ADS-B correlation'
              }
              on={surrogate}
              onToggle={() => {
                // The aircraft already broadcasts its own ADS-B, so the Custodian
                // switches ADS-B Out and TIS-B off when this goes on. Show that
                // now rather than after the save.
                if (!surrogate) {
                  setAdsbOut(false);
                  setTisb(false);
                }
                setSurrogate((v) => !v);
                touch();
              }}
            />
            <OpsRow
              icon={<ShieldCheck size={15} />}
              tone="green"
              name="Ownership Flag"
              meta={
                ownership
                  ? 'Own aircraft — excluded from DAA conflict calc & alerting'
                  : 'Not flagged as own aircraft — treated as a normal DAA target'
              }
              on={ownership}
              onToggle={() => {
                setOwnership((v) => !v);
                touch();
              }}
            />
          </div>
        </Sec>

        {/* Separation distance of concern differs for manned traffic vs. other
            drones, hence the crewed/uncrewed split. The pilot half is backed by
            `GET /pilots/active` for the list and `PUT /config`'s
            `default_pilot_name` / `default_pilot_fs_guid` for the write. */}
        <Sec title="Pilot & Separation Defaults">
          <div className="grid grid-cols-2 gap-1.5 mb-2.5" data-testid="default-pilot">
            <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-2.5 py-[7px]">
              <select
                data-testid="default-pilot-select"
                value={pilot}
                onChange={(e) => {
                  setPilot(e.target.value);
                  touch();
                }}
                className="w-full bg-transparent text-[0.923em] font-bold leading-none outline-none"
              >
                <option value="">— Select —</option>
                {pilotOptions.map((p) => (
                  <option key={p.fs_guid || p.name} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
              <div className="text-[0.615em] uppercase tracking-wide font-mono text-(--color-text3) mt-1">
                Default Pilot
                {pilotsLoading && <span className="text-(--color-text3)"> · loading…</span>}
                {pilotsUnavailable && (
                  <span className="text-(--color-amber)"> · list unavailable</span>
                )}
              </div>
            </div>
            <Field label="FS GUID (auto)" value={pilotGuid ?? '—'} mono />
          </div>

          {/* BE split its NMAC/SWC scalars into the eight crewed/uncrewed x
              horizontal/vertical columns on 2026-08-27, so the two grids below
              are the whole story — the old single-figure NMAC/SWC rows were
              dropped rather than shown alongside as a duplicate of crewed-H.
              EVAL stays a scalar; it has no crewed/uncrewed dimension. */}
          <SubLabel flush>Separation Distances</SubLabel>
          <ParamGrid flush>
            <NumField
              label="EVAL Distance"
              unit="m"
              k="eval_distance_m"
              params={params}
              onEdit={setParam}
              onCommit={commitParam}
            />
          </ParamGrid>

          <SubLabel flush>NMAC — Near Mid-Air Collision</SubLabel>
          <ParamGrid flush>
            <NumField
              label="Crewed H"
              unit="m"
              k="nmac_h_crewed_m"
              params={params}
              onEdit={setParam}
              onCommit={commitParam}
            />
            <NumField
              label="Crewed V"
              unit="m"
              k="nmac_v_crewed_m"
              params={params}
              onEdit={setParam}
              onCommit={commitParam}
            />
            <NumField
              label="Uncrewed H"
              unit="m"
              k="nmac_h_uncrewed_m"
              params={params}
              onEdit={setParam}
              onCommit={commitParam}
            />
            <NumField
              label="Uncrewed V"
              unit="m"
              k="nmac_v_uncrewed_m"
              params={params}
              onEdit={setParam}
              onCommit={commitParam}
            />
          </ParamGrid>

          <SubLabel flush>SWC — Stay Well Clear</SubLabel>
          <ParamGrid flush>
            <NumField
              label="Crewed H"
              unit="m"
              k="swc_h_crewed_m"
              params={params}
              onEdit={setParam}
              onCommit={commitParam}
            />
            <NumField
              label="Crewed V"
              unit="m"
              k="swc_v_crewed_m"
              params={params}
              onEdit={setParam}
              onCommit={commitParam}
            />
            <NumField
              label="Uncrewed H"
              unit="m"
              k="swc_h_uncrewed_m"
              params={params}
              onEdit={setParam}
              onCommit={commitParam}
            />
            <NumField
              label="Uncrewed V"
              unit="m"
              k="swc_v_uncrewed_m"
              params={params}
              onEdit={setParam}
              onCommit={commitParam}
            />
          </ParamGrid>

          {/* ENG-295 — how far THIS aircraft keeps from geofences / obstacles.
              Empty = not set (not 0). A tenant whose Custodian predates the
              field (sandbox / sait until 1.0.22) sends no key at all, so the box
              is read-only there rather than offering a save that would fail. */}
          <SubLabel flush>Keep-out Buffers</SubLabel>
          <ParamGrid flush>
            {d.geofence_buffer_m === undefined ? (
              <Field label="Geofence (m)" value="Not available yet" />
            ) : (
              <NumField
                label="Geofence"
                unit="m"
                k="geofence_buffer_m"
                params={params}
                onEdit={setParam}
                onCommit={commitParam}
              />
            )}
            {d.obstacle_buffer_m === undefined ? (
              <Field label="Obstacle (m)" value="Not available yet" />
            ) : (
              <NumField
                label="Obstacle"
                unit="m"
                k="obstacle_buffer_m"
                params={params}
                onEdit={setParam}
                onCommit={commitParam}
              />
            )}
          </ParamGrid>
        </Sec>

        <Sec title="Data Source Monitoring">
          {d.data_source ? (
            <>
              <FieldGrid>
                <Field
                  label="Status"
                  value={d.data_source.status}
                  tone={
                    d.data_source.status === 'HEALTHY'
                      ? 'ok'
                      : d.data_source.status === 'DEGRADED'
                        ? 'warn'
                        : 'crit'
                  }
                />
                <Field label="Rate" value={dash(d.data_source.rate_hz, ' Hz')} />
                <Field span2 label="Mechanism" value={d.data_source.mechanism} mono />
                <Field
                  span2
                  label="Latency (p50 / p95)"
                  value={`${dash(d.data_source.latency_ms_p50, ' ms')} / ${dash(d.data_source.latency_ms_p95, ' ms')}`}
                />
                <Field span2 label="Last Seen At" value={d.data_source.last_seen_at ?? '—'} mono />
              </FieldGrid>
              <div className="mt-1.5">
                <ParamGrid flush>
                  <NumField
                    label="Max Latency Envelope"
                    unit="ms"
                    k="data_source_max_latency_ms"
                    params={params}
                    onEdit={setParam}
                    onCommit={commitParam}
                  />
                </ParamGrid>
              </div>
            </>
          ) : (
            <div className="text-[0.808em] text-(--color-text3) italic">
              Not monitored — the Custodian has never seen this aircraft.
            </div>
          )}
        </Sec>

        <Sec title="Custodian Configuration">
          <div className="flex flex-wrap gap-1.5 mb-2.5">
            <StatusChip
              ok={d.agent_deployed}
              warn={!d.agent_deployed}
              label={d.agent_deployed ? '✓ Deployed' : '✗ Not Deployed'}
            />
            <StatusChip
              ok={d.custodian_configured}
              warn={!d.custodian_configured}
              label={d.custodian_configured ? '✓ Configured' : '⚠ Not Configured'}
            />
            {d.custodian_runtime && <StatusChip label={d.custodian_runtime} />}
          </div>
          <FieldGrid>
            <Field span2 mono label="FLYsafe-Drone-GUID" value={d.guid} />
            <Field label="GCS Plugin" value={d.gcs_plugin === 'none' ? '—' : d.gcs_plugin} />
            <Field
              label="Plugin Status"
              value={d.gcs_plugin_status === 'none' ? '—' : d.gcs_plugin_status}
              tone={
                d.gcs_plugin_status === 'online'
                  ? 'ok'
                  : d.gcs_plugin_status === 'offline'
                    ? 'crit'
                    : undefined
              }
            />
            <AdapterTypeSelect
              value={adapter}
              storedType={d.adapter_type}
              onChange={(next) => {
                setAdapter(next);
                touch();
              }}
            />
            <Field label="Publish Rate" value={`${d.publish_rate_hz} Hz`} />
            {/* Follows the selection while editing, like the mockup: only a
                MAVLink aircraft has a host / port / protocol connection. */}
            {isMavlinkAdapter(adapter) && (
              <>
                <Field label="Host" value={dash(d.adapter_host)} mono />
                <Field label="Port" value={dash(d.adapter_port)} mono />
                <Field label="Protocol" value={dash(d.adapter_protocol)} mono />
              </>
            )}
            <Field label="C2 Links" value={dash(d.c2_links)} />
            <Field label="Video Feeds" value={dash(d.video_feeds)} />
            <Field label="Link RSSI" value={dash(d.link_rssi_dbm, ' dBm')} />
          </FieldGrid>
        </Sec>

        {/* The ADS-B Out/In toggles themselves are provisioning intent and
            live on the aircraft record; this section holds their config plus
            the observed state the surveillance feed reports back. */}
        <Sec title="Conspicuity Configuration">
          <div className="mb-2.5">
            <SubHead>Observed</SubHead>
            {d.adsb_observed ? (
              <FieldGrid>
                <Field
                  label="Broadcasting"
                  value={d.adsb_observed.broadcasting ? 'Broadcasting' : 'Not Broadcasting'}
                  tone={d.adsb_observed.broadcasting ? 'ok' : 'crit'}
                />
                <Field label="Last Hex" value={d.adsb_observed.last_icao_hex ?? '—'} mono />
                <Field
                  span2
                  label="Seen by CIFIB"
                  value={d.adsb_observed.last_seen_by_cifib ?? '—'}
                  mono
                />
              </FieldGrid>
            ) : (
              <div className="text-[0.769em] text-(--color-text3) italic mt-1.5">
                No surveillance observations — `GET /adsb/status` returns 501 until the CIFIB
                integration lands.
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 items-start">
            <div className="space-y-1.5">
              <SubHead>ADS-B Out</SubHead>
              <Field
                label="ADS-B Out"
                value={d.adsb_out}
                tone={d.adsb_out === 'Enabled' ? 'ok' : undefined}
              />
              <AdsbCodeField
                value={adsbCode}
                error={adsbCodeError}
                onChange={(v) => {
                  setAdsbCode(v);
                  setAdsbCodeError(null);
                  touch();
                }}
              />
              {d.adsb_out === 'Enabled' && (
                <>
                  <Field label="ADS-B Out Device" value={dash(d.adsb_out_device)} mono />
                  <Field
                    label="Hex Mode"
                    value={
                      d.adsb_hex_mode === 'static' ? 'Static (user-entered)' : 'Dynamic (ADDRt)'
                    }
                  />
                  {d.adsb_hex_mode === 'static' && (
                    <Field label="ADS-B Hex" value={d.ec_adsb_hex ?? '—'} mono />
                  )}
                </>
              )}
            </div>
            <div className="space-y-1.5">
              <SubHead>ADS-B In</SubHead>
              <Field
                label="ADS-B In"
                value={d.adsb_in}
                tone={d.adsb_in === 'Active' ? 'ok' : undefined}
              />
              <Field label="Surveillance" value={dash(d.surveillance_mode)} />
            </div>
          </div>
        </Sec>

        <Sec title="Aircraft">
          <FieldGrid>
            <Field
              label="Status"
              value={d.status}
              tone={d.status === 'Airworthy' ? 'ok' : d.status === 'Maintenance' ? 'warn' : 'crit'}
            />
            <Field
              label="Type"
              value={[d.drone_type, d.propulsion_type].filter(Boolean).join(' · ') || '—'}
            />
            <Field label="Brand" value={dash(d.brand)} />
            <Field label="Model" value={dash(d.model)} />
            <Field label="Firmware" value={d.firmware_version || '—'} mono />
            <Field label="Hardware" value={d.hardware_version || '—'} mono />
            <Field label="Weight" value={dash(d.weight_kg, ' kg')} />
            <Field label="Payload Capacity" value={dash(d.payload_capacity_kg, ' kg')} />
            <Field label="Max H Speed" value={dash(d.max_horizontal_speed, ' m/s')} />
            <Field label="Max V Speed" value={dash(d.max_vertical_speed, ' m/s')} />
            <Field label="Max Flight Time" value={dash(d.max_flight_time_min, ' min')} />
            <Field label="Colour" value={dash(d.color)} />
            <Field
              label="Purchase Date"
              value={d.purchase_date ? d.purchase_date.slice(0, 10) : '—'}
            />
            <Field
              label="Insurable Value"
              value={
                d.insurable_value && Number(d.insurable_value) > 0
                  ? `$${Number(d.insurable_value).toLocaleString()}`
                  : '—'
              }
            />
          </FieldGrid>
        </Sec>

        <Sec title="DAA Rules">
          <DaaRules rules={d.daa_rules} serial={base.serial_number} />
        </Sec>

        <Sec title="Custodian API Config">
          {d.yaml_created ? (
            <YamlBlock yaml={d.yaml} />
          ) : (
            <div className="rounded-lg border border-(--color-border) bg-(--color-bg) p-6 flex flex-col items-center gap-2 text-center">
              <Lock size={20} className="text-(--color-text3)" />
              <div className="text-[0.846em] font-bold font-mono">custodian_config.yaml</div>
              <div className="text-[0.769em] text-(--color-text3)">
                No Custodian API Config available for this drone
              </div>
              <button
                type="button"
                onClick={() => toast.info('Configure Custodian', { description: STUB })}
                className="mt-1 h-7 px-3 rounded-md bg-(--color-green) text-(--color-on-accent) text-[0.769em] font-bold"
              >
                Configure Custodian
              </button>
            </div>
          )}
        </Sec>

        {d.notes && (
          <Sec title="Aircraft Notes">
            <p className="text-[0.846em] text-(--color-text2) whitespace-pre-wrap">{d.notes}</p>
          </Sec>
        )}

        <Sec title="Deployment Notes">
          <textarea
            defaultValue=""
            onChange={touch}
            placeholder="Add deployment notes…"
            className="w-full min-h-[64px] resize-y rounded-md border border-(--color-border) bg-(--color-surface) p-2 text-[0.846em] outline-none focus:border-(--color-green)"
          />
        </Sec>
      </div>
    </div>
  );
}

function SubLabel({ children, flush }: { children: React.ReactNode; flush?: boolean }) {
  return (
    <div
      className={cn(
        'text-[0.615em] font-bold uppercase tracking-wide text-(--color-text3) pt-2 pb-1',
        !flush && 'px-3.5',
      )}
    >
      {children}
    </div>
  );
}

function ParamGrid({ children, flush }: { children: React.ReactNode; flush?: boolean }) {
  return <div className={cn('grid grid-cols-2 gap-1.5 pb-2', !flush && 'px-3.5')}>{children}</div>;
}

function NumField({
  label,
  unit,
  k,
  params,
  note,
  onEdit,
  onCommit,
}: {
  label: string;
  unit: string;
  k: ParamKey;
  params: ParamState;
  /** Small extra after the label, e.g. the same value in other units. */
  note?: string;
  onEdit: (k: ParamKey, v: string) => void;
  onCommit: (k: ParamKey) => void;
}) {
  const spec = PARAMS[k] as { min?: number; max?: number };
  const range = spec.min != null && spec.max != null ? `${spec.min}–${spec.max}` : undefined;
  return (
    <div className="rounded-md border border-(--color-border) bg-(--color-surface) px-2.5 py-[5px]">
      <input
        type="number"
        inputMode="decimal"
        value={params[k]}
        placeholder="—"
        onChange={(e) => onEdit(k, e.target.value)}
        onBlur={() => onCommit(k)}
        className="w-full bg-transparent text-[0.923em] font-bold leading-tight outline-none"
      />
      <div className="text-[0.615em] uppercase tracking-wide text-(--color-text3) mt-0.5">
        {label} <span className="opacity-60 normal-case">({unit})</span>
        {range && <span className="opacity-45 normal-case"> · {range}</span>}
        {note && <span className="opacity-60 normal-case"> · {note}</span>}
      </div>
    </div>
  );
}

// ENG-301 — shown on ADS-B Out / TIS-B while the telemetry source is ADS-B.
const LIVEADS_NA = 'Not applicable — this aircraft’s telemetry source is ADS-B (LiveADS)';

/** The Custodian takes metres only; feet are shown beside the box (ENG-285). */
const feetNote = (metres: string | undefined): string | undefined => {
  const v = Number.parseFloat(metres ?? '');
  return v > 0 ? `≈ ${Math.round(v / 0.3048)} ft` : undefined;
};

/** Seconds are what is sent; minutes are what a person thinks in (ENG-286). */
const minutesNote = (seconds: string | undefined): string | undefined => {
  const v = Number.parseFloat(seconds ?? '');
  return v > 0 ? `${+(v / 60).toFixed(1)} min` : undefined;
};

/** The four values the Custodian needs set (non-zero) before OI automation can be on. */
export function missingOiGeometry(params: Record<string, string>): string[] {
  const positive = (v: string | undefined) => Number.parseFloat(v ?? '') > 0;
  const missing: string[] = [];
  if (!positive(params.oi_automation_square_m)) missing.push('Square');
  if (!positive(params.oi_automation_radius_m)) missing.push('Radius');
  if (!positive(params.oi_automation_amend_min_trigger_m)) missing.push('Amend Trigger');
  if (!positive(params.oi_automation_amend_max_alt_agl_m)) missing.push('Amend Max AGL');
  return missing;
}

// ENG-304 — the two rules, worded exactly as the Custodian answers a 422.
const ADSB_CODE_REQUIRED =
  'ADS-B identification code is required when ADS-B Out broadcast is enabled.';
const ADSB_AND_TISB =
  'ADS-B Out and TIS-B rebroadcast cannot both be enabled. Disable one before enabling the other.';

/** ENG-305 — the ADS-B Flight ID / callsign (e.g. `AIRTL25`), not the ICAO hex.
 *  Shaped to what the API accepts so a bad value can't be typed: upper-case,
 *  A-Z and 0-9 only, 8 at most. No `maxLength` attribute on the input: the
 *  browser would cut a pasted "AIR TL-25" to 8 characters BEFORE this strips the
 *  space and dash, losing valid characters. */
export const normaliseAdsbCode = (raw: string): string =>
  raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 8);

function AdsbCodeField({
  value,
  error,
  onChange,
}: {
  value: string;
  error: string | null;
  onChange: (v: string) => void;
}) {
  return (
    <div
      className={cn(
        'rounded-md border bg-(--color-surface) px-2.5 py-[5px]',
        error ? 'border-(--color-red)' : 'border-(--color-border)',
      )}
    >
      <input
        type="text"
        value={value}
        placeholder="Not set"
        autoCapitalize="characters"
        autoComplete="off"
        spellCheck={false}
        aria-label="ADS-B Identification Code"
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange(normaliseAdsbCode(e.target.value))}
        className="w-full bg-transparent font-mono text-[0.808em] font-bold leading-tight outline-none placeholder:font-sans placeholder:font-normal"
      />
      <div className="text-[0.615em] uppercase tracking-wide font-mono text-(--color-text3) mt-0.5">
        ADS-B Identification Code <span className="opacity-60 normal-case">· e.g. AIRTL25</span>
      </div>
      {error && <div className="text-[0.692em] text-(--color-red) mt-0.5">{error}</div>}
    </div>
  );
}

function DTwinStrip({ d }: { d: InventoryDrone }) {
  const s = d.dtwin_state;
  const lowBatt = s.battery_remaining_pct != null && s.battery_remaining_pct < 20;
  return (
    <div className="flex items-center gap-1.5 flex-wrap rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
      <DChip tone={s.adapter_connected ? 'ok' : 'crit'} dot>
        {s.adapter_connected ? 'Connected' : 'Disconnected'}
      </DChip>
      {s.armed && s.flight_mode && <DChip tone="mode">{s.flight_mode}</DChip>}
      <DChip tone={s.armed ? 'crit' : 'ok'} dot>
        {s.armed ? 'Armed' : 'Disarmed'}
      </DChip>
      {s.battery_remaining_pct != null && (
        <DChip tone={lowBatt ? 'crit' : 'ok'}>⚡ {s.battery_remaining_pct}%</DChip>
      )}
      {d.data_source ? (
        <DChip
          tone={
            d.data_source.status === 'HEALTHY'
              ? 'ok'
              : d.data_source.status === 'DEGRADED'
                ? 'warn'
                : 'crit'
          }
        >
          {d.data_source.status}
        </DChip>
      ) : (
        <DChip tone="mode">Not Monitored</DChip>
      )}
      <span className="ml-auto text-[0.692em] font-mono text-(--color-text3)">
        {s.last_telemetry_at
          ? new Date(s.last_telemetry_at).toISOString().replace('.000', '')
          : 'no telemetry'}
      </span>
    </div>
  );
}

function DChip({
  tone,
  dot,
  children,
}: { tone: 'ok' | 'warn' | 'crit' | 'mode'; dot?: boolean; children: React.ReactNode }) {
  const cls =
    tone === 'ok'
      ? 'text-(--color-green) bg-(--color-green-bg) border-(--color-border)'
      : tone === 'warn'
        ? 'text-(--color-amber) bg-(--color-amber-bg) border-(--color-border)'
        : tone === 'crit'
          ? 'text-(--color-red) bg-(--color-red-bg) border-(--color-border)'
          : 'text-(--color-text) bg-(--color-surface) border-(--color-border) font-mono';
  const dotCls =
    tone === 'ok'
      ? 'bg-(--color-green)'
      : tone === 'crit'
        ? 'bg-(--color-red)'
        : 'bg-(--color-amber)';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-[3px] text-[0.769em] font-bold',
        cls,
      )}
    >
      {dot && <span className={cn('w-[7px] h-[7px] rounded-full', dotCls)} />}
      {children}
    </span>
  );
}

function OpsRow({
  icon,
  tone,
  name,
  meta,
  on,
  onToggle,
  disabled,
}: {
  icon: React.ReactNode;
  tone: 'green' | 'blue' | 'violet';
  name: string;
  meta: string;
  on: boolean;
  onToggle: () => void;
  /** Blocks switching ON only; turning off is always allowed. */
  disabled?: boolean;
}) {
  const iconBg =
    tone === 'green'
      ? 'bg-(--color-green-bg) text-(--color-green)'
      : tone === 'blue'
        ? 'bg-(--color-blue-bg) text-(--color-blue)'
        : 'bg-[#7c3aed20] text-[#7c3aed]';
  return (
    <div className="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-(--color-border-lite) last:border-b-0">
      <div
        className={cn(
          'w-[30px] h-[30px] rounded-md flex items-center justify-center shrink-0',
          iconBg,
        )}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[0.923em] font-bold leading-tight">{name}</div>
        <div className="text-[0.692em] font-mono text-(--color-text3) truncate">{meta}</div>
      </div>
      <Toggle on={on} onClick={onToggle} disabled={disabled} />
    </div>
  );
}

function Toggle({
  on,
  onClick,
  disabled,
}: { on: boolean; onClick: () => void; disabled?: boolean }) {
  return <Switch checked={on} onCheckedChange={onClick} disabled={disabled} />;
}

/** DAA rules list, with the add/delete affordances the dedicated endpoints
 *  added on 2026-08-27 (spec §1.5). Rules are NOT part of the `PUT /config`
 *  save, so these mutate immediately rather than joining the panel's dirty
 *  state — there is no Save button to gather them up.
 *
 *  Editing an existing rule is deliberately absent: the API offers only
 *  append and remove, so an "edit" would be a delete + re-add that silently
 *  reorders the array and issues a new id. */
function DaaRules({ rules, serial }: { rules: InventoryDrone['daa_rules']; serial: string }) {
  const addRule = useAddDaaRule();
  const deleteRule = useDeleteDaaRule();
  const [adding, setAdding] = useState(false);
  const busy = addRule.isPending || deleteRule.isPending;

  const onAdd = async (rule: DaaRuleDraft) => {
    if (config.isMock) {
      toast.success('DAA rule added', { description: STUB });
      setAdding(false);
      return;
    }
    try {
      await addRule.mutateAsync({ serial, rule });
      setAdding(false);
      toast.success('DAA rule added');
    } catch {
      toast.error('Failed to add DAA rule');
    }
  };

  const onDelete = async (r: InventoryDrone['daa_rules'][number]) => {
    // `rule_id` is an `R{n}` placeholder when the server record had no `id`
    // — deleting by it would 404 or, worse, match another drone's rule. The
    // control is hidden in that case, so this is belt-and-braces.
    const id = typeof r.raw?.id === 'string' ? r.raw.id : null;
    if (!id) return;
    if (config.isMock) {
      toast.success('DAA rule removed', { description: STUB });
      return;
    }
    try {
      await deleteRule.mutateAsync({ serial, ruleId: id });
      toast.success('DAA rule removed');
    } catch {
      toast.error('Failed to remove DAA rule');
    }
  };

  const enabled = rules.filter((r) => r.enabled).length;
  const header = (
    <div className="flex items-center justify-between gap-2">
      <div className="text-[0.692em] font-mono text-(--color-text3)">
        {rules.length === 0 ? (
          'No rules configured'
        ) : (
          <>
            {rules.length} rules configured ·{' '}
            <span className="text-(--color-green)">{enabled} enabled</span> ·{' '}
            <span className="text-(--color-text3)">{rules.length - enabled} disabled</span>
          </>
        )}
      </div>
      <button
        type="button"
        disabled={busy || adding}
        onClick={() => setAdding(true)}
        className="rounded-md border border-(--color-border) px-2 py-[3px] text-[0.692em] font-bold uppercase tracking-wide hover:bg-(--color-surface) disabled:opacity-50"
      >
        + Add rule
      </button>
    </div>
  );

  return (
    <div className="space-y-2">
      {header}
      {adding && <DaaRuleForm busy={busy} onCancel={() => setAdding(false)} onSubmit={onAdd} />}
      {rules.length === 0 && !adding && (
        <div className="text-[0.808em] text-(--color-text3) italic">No DAA rules configured.</div>
      )}
      {rules.map((r, i) => {
        const p = r.priority.toLowerCase();
        const tint =
          p === 'critical'
            ? 'bg-(--color-red-bg)'
            : p === 'high'
              ? 'bg-(--color-amber-bg)'
              : 'bg-(--color-surface)';
        const col =
          p === 'critical'
            ? 'text-(--color-red)'
            : p === 'high'
              ? 'text-(--color-amber)'
              : 'text-(--color-text2)';
        return (
          <div
            key={r.rule_id}
            className={cn(
              'rounded-md border border-(--color-border) p-2.5',
              tint,
              !r.enabled && 'opacity-60',
            )}
          >
            <div className={cn('flex items-center justify-between text-[0.769em] font-bold', col)}>
              <span>
                Rule {i + 1} — {r.priority}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-[0.85em] font-mono text-(--color-text3)">
                  {r.enabled ? 'enabled' : 'disabled'}
                </span>
                {typeof r.raw?.id === 'string' && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onDelete(r)}
                    aria-label={`Remove rule ${i + 1}`}
                    className="rounded px-1 text-[0.85em] font-mono text-(--color-text3) hover:text-(--color-red) disabled:opacity-50"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            <DaaRuleBody rule={r} colorClass={col} />
          </div>
        );
      })}
    </div>
  );
}

/** Keys `toDaaRule` already projects onto the card's own fields. Excluded from
 *  the free-form fallback so it doesn't repeat what is shown directly above. */
const DAA_PROJECTED_KEYS = new Set(['id', 'condition', 'action', 'priority', 'enabled']);

/** Renders a rule's condition/result when the record uses the conventional
 *  shape, and falls back to listing whatever keys it does carry when it
 *  doesn't.
 *
 *  `daa_rules` is a free-form jsonb array (spec §1.5) — only `id` is
 *  guaranteed. Before the fallback, a rule written by any other producer
 *  rendered as two empty lines and an arrow. */
function DaaRuleBody({
  rule,
  colorClass,
}: {
  rule: InventoryDrone['daa_rules'][number];
  colorClass: string;
}) {
  if (rule.condition || rule.result) {
    return (
      <>
        <div className="text-[0.808em] text-(--color-text2) mt-1">{rule.condition || '—'}</div>
        <div className={cn('text-[0.808em] font-semibold mt-0.5', colorClass)}>
          → {rule.result || '—'}
        </div>
      </>
    );
  }

  const extra = Object.entries(rule.raw ?? {}).filter(([k]) => !DAA_PROJECTED_KEYS.has(k));
  if (extra.length === 0) {
    return (
      <div className="text-[0.808em] text-(--color-text3) italic mt-1">
        Rule carries no readable fields.
      </div>
    );
  }
  return (
    <dl className="mt-1 space-y-0.5">
      {extra.map(([k, v]) => (
        <div key={k} className="flex gap-2 text-[0.769em]">
          <dt className="font-mono text-(--color-text3) shrink-0">{k}</dt>
          <dd className="text-(--color-text2) break-all">
            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** What the add form collects. The endpoint takes any JSON object, so this is
 *  the conventional shape the cards render — not a limit the API imposes.
 *  Extends `DaaRuleRecord` so a draft is directly submittable; `id` stays
 *  absent, since the server generates it. */
interface DaaRuleDraft extends DaaRuleRecord {
  condition: string;
  action: string;
  priority: string;
  enabled: boolean;
}

const DAA_PRIORITIES = ['critical', 'high', 'medium'] as const;

function DaaRuleForm({
  busy,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  onCancel: () => void;
  onSubmit: (rule: DaaRuleDraft) => void;
}) {
  const [condition, setCondition] = useState('');
  const [action, setAction] = useState('');
  const [priority, setPriority] = useState<string>('medium');

  const canSubmit = condition.trim() !== '' && action.trim() !== '' && !busy;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit({
          condition: condition.trim(),
          action: action.trim(),
          priority,
          enabled: true,
        });
      }}
      className="rounded-md border border-(--color-border) bg-(--color-surface) p-2.5 space-y-2"
    >
      <input
        value={condition}
        onChange={(e) => setCondition(e.target.value)}
        placeholder="Condition — e.g. intruder within 500 m"
        className="w-full rounded border border-(--color-border) bg-(--color-bg) px-2 py-1 text-[0.808em] outline-none"
      />
      <input
        value={action}
        onChange={(e) => setAction(e.target.value)}
        placeholder="Action — e.g. hold position and alert PIC"
        className="w-full rounded border border-(--color-border) bg-(--color-bg) px-2 py-1 text-[0.808em] outline-none"
      />
      <div className="flex items-center justify-between gap-2">
        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          className="rounded border border-(--color-border) bg-(--color-bg) px-2 py-1 text-[0.769em] outline-none"
        >
          {DAA_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-(--color-border) px-2 py-[3px] text-[0.692em] font-bold uppercase tracking-wide hover:bg-(--color-bg)"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-md border border-(--color-border) bg-(--color-bg) px-2 py-[3px] text-[0.692em] font-bold uppercase tracking-wide disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Add'}
          </button>
        </div>
      </div>
    </form>
  );
}

function YamlBlock({ yaml: raw }: { yaml: string }) {
  const yaml = raw.trimEnd();
  const [saved, setSaved] = useState(yaml);
  const [text, setText] = useState(yaml);
  const [status, setStatus] = useState<{ tone: 'ok' | 'warn' | 'crit'; text: string }>({
    tone: 'ok',
    text: 'No pending changes',
  });
  const [restartable, setRestartable] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [confirm, setConfirm] = useState<'save' | 'restart' | null>(null);
  const [touched, setTouched] = useState(false);
  const dirty = text !== saved;

  // Track new server-generated config until the operator touches the buffer;
  // from then on the local text owns it. Keying off `dirty` instead would
  // re-adopt the server string the moment a Save cleared the dirty flag,
  // silently throwing away the config that was just staged. `DroneDetail` is
  // keyed by drone id, so this remounts (and re-syncs) per aircraft.
  useEffect(() => {
    if (touched) return;
    setSaved(yaml);
    setText(yaml);
  }, [yaml, touched]);

  const onEdit = (v: string) => {
    setText(v);
    setTouched(true);
  };

  const onDiscard = () => {
    setText(saved);
    setStatus({ tone: 'ok', text: 'No pending changes' });
  };

  const doSave = () => {
    setConfirm(null);
    if (text.trim().length < 10) {
      setStatus({ tone: 'crit', text: 'File empty — not saved' });
      return;
    }
    setSaved(text);
    setRestartable(true);
    setStatus({ tone: 'ok', text: 'Saved · Restart agent to apply' });
    toast.success('Configuration staged', { description: YAML_STUB });
  };

  const doRestart = () => {
    setConfirm(null);
    setRestarting(true);
    setStatus({ tone: 'warn', text: 'Agent restart in progress…' });
    setTimeout(() => {
      setRestarting(false);
      setRestartable(false);
      setStatus({ tone: 'ok', text: 'Agent restarted · Configuration applied ✓' });
      toast.success('Custodian restarted', { description: YAML_STUB });
    }, 2200);
  };

  // A pending edit always outranks whatever the last action left behind —
  // otherwise the bar keeps saying "No pending changes" while the Unsaved
  // badge is lit right above it.
  const shown = dirty
    ? { tone: 'warn' as const, text: 'Unsaved changes — click Save to apply' }
    : status;
  const dotCls =
    shown.tone === 'ok'
      ? 'bg-(--color-green)'
      : shown.tone === 'warn'
        ? 'bg-(--color-amber)'
        : 'bg-(--color-red)';

  return (
    <div className="rounded-lg border border-(--color-border) overflow-hidden">
      {/* Filename + badges + 3 buttons don't fit one line in a narrow panel;
          wrapping keeps the action row inside the card instead of pushing
          "Restart Custodian" past its right edge. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 bg-(--color-bg) border-b border-(--color-border-lite)">
        <span className="text-[0.769em] font-mono font-bold min-w-0 break-all">
          custodian_config.yaml
        </span>
        <span className="text-[0.615em] font-bold px-1.5 py-0.5 rounded bg-(--color-green-bg) text-(--color-green) shrink-0">
          Edit Access
        </span>
        {dirty && (
          <span className="text-[0.615em] font-bold px-1.5 py-0.5 rounded bg-(--color-amber-bg) text-(--color-amber) shrink-0">
            Unsaved
          </span>
        )}
        <div className="ml-auto flex flex-wrap gap-1 justify-end">
          {dirty && (
            <button
              type="button"
              onClick={onDiscard}
              className="h-5 px-2 rounded text-[0.615em] font-bold bg-(--color-bg) border border-(--color-border) text-(--color-text2) hover:text-(--color-text)"
            >
              Discard
            </button>
          )}
          <button
            type="button"
            disabled={!dirty}
            onClick={() => setConfirm('save')}
            className={cn(
              'h-5 px-2 rounded text-[0.615em] font-bold',
              dirty
                ? 'bg-(--color-green) text-(--color-on-accent)'
                : 'bg-(--color-bg) text-(--color-text3) cursor-not-allowed',
            )}
          >
            Save
          </button>
          {/* Restart is meaningless until a config is staged, and unsafe to
              offer while an unsaved edit is pending — the mockup locks it in
              both states. */}
          <button
            type="button"
            disabled={!restartable || dirty || restarting}
            onClick={() => setConfirm('restart')}
            className={cn(
              'h-5 px-2 rounded text-[0.615em] font-bold',
              restartable && !dirty && !restarting
                ? 'bg-(--color-red) text-(--color-on-accent)'
                : 'bg-(--color-bg) text-(--color-text3) cursor-not-allowed',
            )}
          >
            {restarting ? 'Restarting…' : 'Restart Custodian'}
          </button>
        </div>
      </div>

      <textarea
        spellCheck={false}
        value={text}
        onChange={(e) => onEdit(e.target.value)}
        className="block w-full min-h-[260px] resize-y px-3 py-2.5 text-[0.692em] leading-relaxed font-mono outline-none whitespace-pre overflow-x-auto"
        style={{ background: '#0d1008', color: '#8fbd8a' }}
      />

      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-(--color-bg) border-t border-(--color-border-lite) text-[0.615em] font-mono text-(--color-text3)">
        <span className={cn('w-[7px] h-[7px] rounded-full shrink-0', dotCls)} />
        {shown.text}
        <span className="ml-auto shrink-0">Lines: {text.split('\n').length}</span>
      </div>

      <Dialog open={confirm != null} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirm === 'restart' ? 'Restart Aircraft Custodian?' : 'Save Configuration?'}
            </DialogTitle>
            <DialogDescription>
              {confirm === 'restart'
                ? 'The Custodian will disconnect briefly (~2–3 seconds) and reconnect with the updated configuration. Ensure the aircraft is in a safe hover or on the ground before restarting.'
                : 'You are about to save changes to custodian_config.yaml. The Custodian must be restarted for changes to take effect. Unsaved changes cannot be recovered.'}
            </DialogDescription>
          </DialogHeader>
          <p className="text-[0.769em] text-(--color-amber)">{YAML_STUB}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>
              {confirm === 'restart' ? 'Not Now' : 'Cancel'}
            </Button>
            <Button
              variant={confirm === 'restart' ? 'danger' : 'default'}
              onClick={confirm === 'restart' ? doRestart : doSave}
            >
              {confirm === 'restart' ? 'Restart Custodian' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ───────────────────────── User detail ───────────────────────── */

function UserDetail({ u }: { u: InventoryUser }) {
  return (
    <div className="flex flex-col h-full">
      <PanelHeader title={u.full_name}>
        <StatusChip ok={u.status === 'ACTIVE'} warn={u.status !== 'ACTIVE'} label={u.status} />
        {u.is_admin && <StatusChip label="Admin" tone="info" />}
        <button
          type="button"
          onClick={() => toast.info('Edit user', { description: STUB })}
          className="h-6 px-2.5 rounded-md bg-(--color-bg) text-(--color-text2) text-[0.692em] font-bold flex items-center gap-1 shrink-0 hover:text-(--color-text)"
        >
          <Pencil size={11} /> Edit
        </button>
      </PanelHeader>

      <div className="flex-1 overflow-y-auto p-3.5 space-y-6">
        <Sec title="Account">
          <FieldGrid>
            <Field span2 label="Full Name" value={u.full_name} />
            <Field
              label="Status"
              value={u.status}
              tone={u.status === 'ACTIVE' ? 'ok' : undefined}
            />
            <Field
              label="Country / Lang"
              value={dash([u.country, u.language?.toUpperCase()].filter(Boolean).join(' · '))}
            />
            <Field span2 label="Email / Login" value={u.email} mono />
            <Field label="Phone" value={u.phone ?? '—'} mono />
            {/* The live source (`GET /pilots/active`) carries none of these
                three. They render '—' rather than a derived or invented
                value — see `toInventoryUser`. */}
            <Field label="Timezone" value={dash(u.timezone)} />
            <Field
              label="Last Login"
              value={u.last_login ? new Date(u.last_login).toLocaleString() : '—'}
            />
            <Field
              label="Created"
              value={u.date_created ? new Date(u.date_created).toLocaleDateString() : '—'}
            />
          </FieldGrid>
        </Sec>

        <Sec title="Certification">
          <FieldGrid>
            <Field label="Legal Profile" value={u.legal_profile ?? '—'} />
            <Field label="Licence Type" value={u.license_type ?? '—'} />
            <Field span2 label="Licence Number" value={u.license_number ?? '—'} mono />
          </FieldGrid>
        </Sec>

        <Sec title="Roles">
          <div className="flex flex-wrap gap-1">
            {u.roles.map((r) => (
              <span
                key={r}
                className="text-[0.769em] font-semibold px-[7px] py-0.5 rounded-[3px] bg-(--color-bg) border border-(--color-border) text-(--color-text2)"
              >
                {r}
              </span>
            ))}
          </div>
        </Sec>

        <Sec title="System">
          <FieldGrid>
            <Field span2 label="GUID" value={u.guid} mono />
            {u.notes && <Field span2 label="Notes" value={u.notes} />}
          </FieldGrid>
        </Sec>
      </div>
    </div>
  );
}

/* ───────────────────────── Shared bits ───────────────────────── */

function PanelHeader({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <header className="px-3 py-2.5 border-b border-(--color-border) shrink-0 flex items-center gap-2 min-h-[45px]">
      <span className="text-[0.923em] font-bold truncate">{title}</span>
      {children && (
        <div className="ml-auto flex items-center gap-1.5 flex-wrap justify-end">{children}</div>
      )}
    </header>
  );
}

/** Active mission binding for one aircraft (`GET /{serial}/mission`).
 *
 *  Three states, all distinct on purpose:
 *   - `data` present  → the binding, field by field ('—' per missing field,
 *     since population is uneven: older bindings carry only `mission_name` +
 *     `oi_window` while a live one carries pilot, type and location too).
 *   - `data === null` → HTTP 404, i.e. this drone has no active binding. That
 *     is normal for 24 of 27 aircraft, so it reads as a status, not a failure.
 *   - `isError`       → the request actually failed. Never conflated with the
 *     404 above, or "no binding" would hide a broken endpoint.
 */
function MissionBinding({ serial }: { serial: string }) {
  const { data, isPending, isError } = useCustodianMission(serial);

  if (config.isMock) {
    return <EmptyNote>Mission bindings come from the Custodian API — live mode only.</EmptyNote>;
  }
  if (isPending) return <EmptyNote>Loading binding…</EmptyNote>;
  if (isError)
    return <EmptyNote tone="crit">Couldn't reach the mission-binding endpoint.</EmptyNote>;
  if (!data) return <EmptyNote>No active mission binding for this aircraft.</EmptyNote>;

  const w = data.oi_window ?? null;
  const vertices = missionVolumeVertexCount(w?.operational_volume);
  const utmState = w?.utm_state ?? null;

  return (
    <FieldGrid>
      <Field span2 label="Mission" value={data.mission_name || '—'} />
      <Field label="Type" value={data.mission_type || '—'} />
      <Field label="Pilot" value={data.pilot_full_name || '—'} />
      <Field span2 label="Location" value={data.mission_location || '—'} />
      <Field
        span2={utmStateTone(utmState) === 'crit' || utmState === 'held'}
        label="UTM State"
        value={utmStateLabel(utmState) ?? '—'}
        tone={utmStateTone(utmState)}
      />
      <Field label="OI Volume" value={vertices != null ? `${vertices} vertices` : '—'} />
      <Field span2 label="UTM Declaration" value={w?.utm_declaration_id || '—'} mono />
      <Field label="Bound" value={fmtBindingTs(data.created_at)} mono />
      <Field label="Last Message" value={fmtBindingTs(data.last_message_at)} mono />
      <Field span2 label="Mission GUID" value={data.mission_guid || '—'} mono />
    </FieldGrid>
  );
}

/** Binding timestamps arrive without a zone suffix (e.g.
 *  `2026-08-24T05:25:11.089923`). They are UTC per the API, so they are
 *  rendered as an explicit UTC instant rather than being handed to the
 *  browser's local-time parser, which would silently shift them. */
function fmtBindingTs(iso: string | null): string {
  if (!iso) return '—';
  const normalised = /(Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;
  const ms = Date.parse(normalised);
  if (Number.isNaN(ms)) return iso;
  return `${new Date(ms).toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

function EmptyNote({ children, tone }: { children: React.ReactNode; tone?: 'crit' }) {
  return (
    <div
      className={cn(
        'rounded-md border border-(--color-border) bg-(--color-bg) px-2.5 py-2 text-[0.769em]',
        tone === 'crit' ? 'text-(--color-red)' : 'text-(--color-text3)',
      )}
    >
      {children}
    </div>
  );
}

function Sec({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="text-[0.769em] font-bold uppercase tracking-wide text-(--color-text2) mb-2 pb-1 border-b border-(--color-border-lite)">
        {title}
      </div>
      {children}
    </section>
  );
}

function FieldGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-1.5">{children}</div>;
}

function Field({
  label,
  value,
  span2,
  mono,
  tone,
}: {
  label: string;
  value: string | number;
  span2?: boolean;
  mono?: boolean;
  tone?: 'ok' | 'warn' | 'crit';
}) {
  const toneCls =
    tone === 'ok'
      ? 'text-(--color-green)'
      : tone === 'warn'
        ? 'text-(--color-amber)'
        : tone === 'crit'
          ? 'text-(--color-red)'
          : 'text-(--color-text)';
  return (
    <div
      className={cn(
        'rounded-md border border-(--color-border) bg-(--color-bg) px-2.5 py-[7px]',
        span2 && 'col-span-2',
      )}
    >
      <div
        className={cn(
          'text-[0.923em] font-bold leading-none break-all',
          toneCls,
          mono && 'font-mono text-[0.808em]',
        )}
      >
        {value}
      </div>
      <div className="text-[0.615em] uppercase tracking-wide font-mono text-(--color-text3) mt-1">
        {label}
      </div>
    </div>
  );
}

function SubHead({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[0.692em] font-bold uppercase tracking-wide text-(--color-text2) pb-1 border-b border-(--color-border-lite)">
      {children}
    </div>
  );
}

function StatusChip({
  ok,
  warn,
  label,
  tone,
}: { ok?: boolean; warn?: boolean; label: string; tone?: 'info' }) {
  const cls =
    tone === 'info'
      ? 'bg-(--color-blue-bg) text-(--color-blue)'
      : ok
        ? 'bg-(--color-green-bg) text-(--color-green)'
        : warn
          ? 'bg-(--color-amber-bg) text-(--color-amber)'
          : 'bg-(--color-bg) text-(--color-text2) border border-(--color-border)';
  return (
    <span
      className={cn(
        'shrink-0 px-1.5 py-0.5 rounded text-[0.615em] font-bold whitespace-nowrap',
        cls,
      )}
    >
      {label}
    </span>
  );
}
